import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import useSWR, { SWRConfig } from "swr";
// components
import BreadCrumb from "@/components/global/BreadCrumb";
import MainBanners from "@/components/global/swiper/MainBanners";
import CategoryGridSwiper from "@/components/global/swiper/CategoryGridSwiper";
import SideMenu from "@/components/category/SideMenu";
import ResponsiveButton from "@/components/category/ResponsiveButton";
import CategoryProducts from "@/components/category/CategoryProducts";

// services
import { fetchHandshake } from "@/services/handshake";
import {
  getFilterableAttributes,
  getProductsByCategoryId,
  getTopCategoryAndBrand,
} from "@/services/category";

// Hooks
import useInfiniteProducts from "@/hooks/category/useInfiniteProducts";
import useFilterProducts from "@/hooks/category/useFilterProducts";
import useSortProducts from "@/hooks/category/useSortProducts";
import useHandleMainBannersData from "@/hooks/category/useHandleMainBannersData";

// others
import { useGlobalData } from "@/context/global";
import CategoryBanners from "@/components/category/CategoryBanners";
import ProductSwiper from "@/components/global/swiper/ProductSwiper";
import useTopCategoryTabs from "@/hooks/category/useTopCategoryTabs";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { findObjectByIdV2 } from "@/utils/global";
import Head from "next/head";
import EmptyComp from "@/components/global/EmptyComp";
import EmptyCategoryIcon from "@/components/icons/EmptyCategoryIcon";
import Seo from "@/components/seo";
import { useEffect, useMemo, useState } from "react";
import { getObjectsInData } from "@/utils/category";
import TopDealsSwiper from "@/components/global/swiper/TopDealsSwiper";
import TopDealswiper from "@/components/global/swiper/TopDealswiper";
import { getMultiProductsRating } from "@/services/product/reviews";
import { event_sub_item_list, event_view_item_list } from "@/utils/gtm";
import { productSkuString, skusArrayHandler } from "@/helpers/product";
import { getCookie } from "cookies-next";
import { useClient } from "@/hooks/useClient";
import { sortKeys } from "@/constants/Category";
import { useSlugs } from "@/context/categoryTree";
// import { event_view_item_list } from "@/utils/gtm";

const CategoryPage = ({ products, categoryID, aggregate }) => {
  const { t } = useTranslation("category");
  const context = useGlobalData();
  const { query, locale } = useRouter();
  const handshake = getCookie("handshake");
  const [catProducts, setCatProducts] = useState(products || []);
  const [filters, setFilters] = useState(aggregate);
  const [topCategoryTabs, setTopCategoryTabs] = useState([]);
  const [fetchCategoryProductsRating, setFetchCategoryProductsRating] =
    useState(false);
  const [fetchTopCategoryAndBrands, setFetchTopCategoryAndBrands] =
    useState(false);
  const [fetchTopCategoryProducts, setFetchTopCategoryProducts] =
    useState(false);
  const [fetchFilterableAttributes, setFetchFilterableAttributes] =
    useState(false);
  const { isClient } = useClient();

  useEffect(() => {
    if (products?.length > 0) {
      setCatProducts(products);
    }
  }, [products]);

  // get products ratings
  const productsSku = skusArrayHandler(
    products.map((product) => productSkuString(product?.sku))
  );

  useSWR(
    fetchFilterableAttributes
      ? `filterable-attributes/${productsSku}/${locale}`
      : null,
    () => getFilterableAttributes(handshake, categoryID),
    {
      revalidateOnFocus: false,
      onSuccess: (res) => {
        const categoriesFilter = (res?.data?.data?.at(0)?.attributes || []).map(
          ({ code, label, options }) => ({
            label,
            filter_key: code,
            filterable_values: options
              .map(({ count, value, label }) => ({
                DocCount: count,
                Key: value,
                Label: label,
              }))
              ?.filter(
                (option) => option?.Key && option?.DocCount && option?.Label
              ),
          })
        );
        setFilters([...categoriesFilter, ...aggregate]);
      },
    }
  );

  useSWR(
    fetchCategoryProductsRating
      ? `category-products-rating/${productsSku}/${locale}`
      : null,
    () => getMultiProductsRating(productsSku, handshake),
    {
      revalidateOnFocus: false,
      revalidateOnMount: productsSku?.length > 0,
      onSuccess: (res) => {
        setCatProducts((prev) =>
          prev?.map((product) => ({
            ...product,
            rating: Object.entries(res?.data?.data?.at(1) || {})
              ?.find((r) => r?.at(0) === product?.sku)
              ?.at(1) || { count: "0", rating: "0.0" },
          }))
        );
      },
    }
  );

  const {
    allProducts,
    setAllProducts,
    allFilterKeys,
    setAllFilterKeys,
    allSortingKeys,
    setAllSortingKeys,
  } = useInfiniteProducts({ products: catProducts, categoryID });

  const {
    selectedFilterKeys,
    setSelectedFilterKeys,
    handleFilter,
    handleResetFilter,
    allFilters,
  } = useFilterProducts({
    setAllFilterKeys,
    setAllProducts,
    categoryID,
    allSortingKeys,
    filters,
  });

  const { handleSorting } = useSortProducts({
    setAllSortingKeys,
    setAllProducts,
    categoryID,
    allSortingKeys,
    allFilterKeys,
  });

  const currentCategory = findObjectByIdV2(
    context?.appData?.categoryData?.children_data || [],
    "url_key",
    query?.url_key?.at(-1)
  );
  const [sortName, setSortName] = useState(
    sortKeys?.find((item) => item?.key === allSortingKeys)?.label
  );
  const { slugs } = useSlugs();
  useEffect(() => {
    setTimeout(() => {
      const pathSegments = slugs?.[currentCategory?.id]?.split("/");
      if (catProducts?.length > 0) {
        if (pathSegments?.length === 1) event_view_item_list(currentCategory);
        else {
          const parentCategory = findObjectByIdV2(
            context?.appData?.categoryData?.children_data || [],
            "url_key",
            pathSegments?.at(-2)
          );
          event_sub_item_list(currentCategory, parentCategory);
        }
      }
    }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // get top category and brands
  const { data: topCategoryAndBrand } = useSWR(
    fetchTopCategoryAndBrands
      ? `top-category-and-brands/${categoryID}/${locale}`
      : null,
    () => getTopCategoryAndBrand(handshake, categoryID),
    {
      revalidateOnFocus: false,
      revalidateOnMount: !!categoryID,
      onSuccess: (res) => {
        setTopCategoryTabs(res?.data?.data?.categoryTabs || []);
      },
    }
  );

  const brands = useMemo(
    () =>
      topCategoryAndBrand?.data?.data?.brands?.flatMap(
        (item) => item?.brandTabs
      ) || [],
    [topCategoryAndBrand]
  );

  useSWR(
    fetchTopCategoryProducts
      ? `top-category-products/${topCategoryTabs?.at(0)?.category_id}/${locale}`
      : null,
    () =>
      getProductsByCategoryId(
        handshake,
        topCategoryTabs?.at(0)?.category_id,
        30,
        0
      ),
    {
      revalidateOnFocus: false,
      revalidateOnMount:
        topCategoryTabs?.length > 0 && Array.isArray(topCategoryTabs),
      onSuccess: (res) => {
        setTopCategoryTabs((prev) =>
          prev?.map((item, key) => ({
            ...item,
            products: key == 0 ? res?.data?.data?.products || [] : [],
          }))
        );
      },
    }
  );

  useEffect(() => {
    setTimeout(() => {
      setFetchCategoryProductsRating(true);
      setFetchTopCategoryAndBrands(true);
      setFetchTopCategoryProducts(true);
      setFetchFilterableAttributes(true);
    }, 5000);
  }, []);

  const BreadCrumbItems = (query?.url_key || [])?.map((item) => {
    return {
      name:
        isClient &&
        findObjectByIdV2(
          context?.appData?.categoryData?.children_data,
          "url_key",
          item
        )?.name,
      url: query?.url_key
        ?.slice(0, query?.url_key?.indexOf(item) + 1)
        .join("/"),
      active: item === query?.url_key?.at(-1),
      //last: item === query?.url_key?.at(-1),
      key: item,
      //type: "category",
    };
  });

  const { updateTopCategoryTabs, handleSwitchTabs, isTopCategoryTabsLoading } =
    useTopCategoryTabs({ topCategoryTabs });

  const { handleMainBannersData } = useHandleMainBannersData({
    context,
    categoryID,
  });
  const childrenData = context?.appData?.categoryData?.children_data;
  const handleSubCategoryData = useMemo(
    () =>
      Array.isArray(childrenData) && childrenData?.length
        ? getObjectsInData(
            childrenData?.filter((item) => item?.id == categoryID)
          )
        : [],
    [categoryID, childrenData]
  );

  return (
    <SWRConfig value={{ provider: () => new Map() }}>
      <Head>
        <title>{currentCategory?.name || ""}</title>
        <meta
          name="description"
          content={currentCategory?.meta_description || ""}
        />
        <meta name="keyword" content={currentCategory?.meta_keyword || ""} />
      </Head>
      <Seo
        title={currentCategory?.name || ""}
        description={currentCategory?.meta_description || ""}
        image={null}
      />
      <BreadCrumb items={BreadCrumbItems} />
      {catProducts?.length ? (
        <div className="grid w-11/12 grid-cols-12 gap-5 mx-auto my-4 sm:my-8">
          <SideMenu
            {...{
              category: currentCategory,
              selectedFilterKeys,
              setSelectedFilterKeys,
              filters: allFilters,
              handleFilter,
              handleResetFilter,
              t,
            }}
          />
          <div className="col-span-full lg:col-span-9 *:mb-5">
            <div className="flex items-center gap-4 overflow-x-auto hide-scroll-container">
              {currentCategory?.feature_categories ? (
                <TopDealswiper
                  data={currentCategory?.feature_categories || []}
                />
              ) : null}
              {/* {[...Array(20).keys()].map((i) => (
                <span
                  key={i}
                  className="px-5 text-sm rounded-md whitespace-nowrap uppercase border py-1.5 font-semibold"
                >
                  Top Deals
                </span>
              ))} */}
            </div>
            {/* Hide These Components when clicking to any option in side menu */}
            <ResponsiveButton
              {...{
                setSelectedFilterKeys,
                selectedFilterKeys,
                filters: allFilters,
                handleFilter,
                handleResetFilter,
                handleSorting,
                allSortingKeys,
                setSortName,
                t,
              }}
            />
            {selectedFilterKeys?.length == 0 ? (
              <>
                <TopDealsSwiper
                  data={handleSubCategoryData?.filter(
                    (item) => item?.top_deals
                  )}
                />
                <div className="sm:mb-9">
                  <MainBanners fromCategories={handleMainBannersData} />
                </div>
                <CategoryGridSwiper
                  handleSubCategoryData={handleSubCategoryData}
                />
                {updateTopCategoryTabs?.length ? (
                  <div className="sm:mb-9">
                    <ProductSwiper
                      {...{
                        fromCategory: updateTopCategoryTabs,
                        handleSwitchTabs,
                        isTopCategoryTabsLoading,
                      }}
                    />
                  </div>
                ) : null}
                {brands?.length ? <CategoryBanners brands={brands} /> : null}
              </>
            ) : null}
            {/* Hide These Components when clicking to any option in side menu */}
            <CategoryProducts
              allProducts={allProducts}
              handleSorting={handleSorting}
              t={t}
              name={currentCategory?.name}
              allSortingKeys={allSortingKeys}
              setSortName={setSortName}
              sortName={sortName}
            />
            <div id="afterProductsSection" className="w-full"></div>
          </div>
        </div>
      ) : (
        <EmptyComp
          title={t("empty category title")}
          description={t("empty category desc")}
          icon={<EmptyCategoryIcon className="w-44 max-sm:h-44 sm:w-72" />}
        />
      )}
    </SWRConfig>
  );
};

export default CategoryPage;

export async function getServerSideProps(ctx) {
  const {
    params: { url_key },
    req,
    res,
    locale,
  } = ctx;
  // get handshake from cookies or generate new handshake
  const handshake = await fetchHandshake({ req, res, locale });

  // parse category id
  const parsedCategoryId = url_key
    ?.at(-1)
    ?.slice(
      url_key?.at(-1)?.lastIndexOf("-c-") + 3,
      url_key?.at(-1)?.length - url_key?.at(-1)?.lastIndexOf("?")
    );

  const { data: productsData, error: productsError } =
    await getProductsByCategoryId(handshake, parsedCategoryId, 30, 0);

  if (productsError) {
    return {
      notFound: true,
    };
  }
  // const filters = await getFilterableAttributes(handshake, parsedCategoryId);
  // const categoriesFilter = (filters?.data?.data?.at(0)?.attributes || []).map(
  //   ({ code, label, options }) => ({
  //     label,
  //     filter_key: code,
  //     filterable_values: options
  //       .map(({ count, value, label }) => ({
  //         DocCount: count,
  //         Key: value,
  //         Label: label,
  //       }))
  //       ?.filter((option) => option?.Key && option?.DocCount && option?.Label),
  //   })
  // );

  // console.log('filters data////////////', filters?.data?.data?.at(0)?.attributes)

  return {
    props: {
      products: productsData?.data?.products?.length
        ? productsData?.data?.products
        : [],
      // filters: [...categoriesFilter, ...productsData?.data?.aggregate] || [],
      categoryID: parsedCategoryId,
      aggregate: productsData?.data?.aggregate || [],
      ...(await serverSideTranslations(locale, ["common", "category"])),
    },
  };
}
